(ns frontend.view.nev.fund.view
  (:require
   [frontend.actions.common :as common-actions]
   [reagent.core :as r]
   [potpuri.core :as p :refer [fn->]]
   [common.localization :as loc]
   [clojure.string :as string]
   [frontend.logging :as logging]
   [frontend.view.entity-viewers.header :as header]
   [frontend.view.field-templates :as templates]
   [frontend.view.nev.events :as events]
   [common.entity :as entity]
   [frontend.view.entity-viewers.entity-editor :as entity-editor]
   [frontend.ui.page.lp-report-editor.event :as lp-report-editor-event]
   [common.util :as cu]
   [frontend.utils :as fu]
   [frontend.view.not-found :as not-found]
   [frontend.routing :as routing]
   [frontend.view.entity-viewers.event-pane :as event-pane]
   [frontend.view.entity-viewers.modules :as modules]
   [frontend.state.current-session :as session]
   [frontend.page-components.zap-toolbar :as zap-toolbar]
   [frontend.view.entity-viewers.utils :as entity-utils]
   [frontend.view.syncing-data :as syncing]
   [frontend.db :as app-db]
   [frontend.view.entity-viewers.components :as nev-components]
   [common.schema.field :as field]
   [frontend.state.permissions :as f-permissions]
   [common.features.plan :as plan]
   [frontend.view.entity-viewers.modules.associated-entities :as associated-entities]
   [frontend.page-components.default-page-layout :as default-page-layout]
   [frontend.view.entity-viewers.modules.fees :as fees]
   [frontend.ui.page.fund.view :as fund-ui]
   [re-frame.core :as rf]
   [frontend.ui.page.fund.sub :as sub]
   [frontend.ui.page.fund.core :as controller]
   [frontend.ui.page.fund.event :as event]
   [frontend.ui.page.fund.investor-references.view :as investor-references]
   [common.teams :as teams]
   [common.schema.registry :as registry]
   [frontend.ui.page.fund.stop-reporting :as stop-reporting]
   [ui.components.alert.view :as alert]
   [ui.components.button.view :as button]
   [ui.components.radix-ui.dropdown-menu.view :as dropdown-menu]))

(defonce timeout-state (atom {}))

(defn toolbar-buttons
  [{:keys [entity] :as _view-args}]
  (let [entity-id (:db/id entity)
        flagged? (contains? @session/flagged-entity-ids
                            entity-id)
        entity-graph-feature? @(r/track session/entity-graph?)]
    (concat

     [(when-not (:common/disabled? entity)
        (zap-toolbar/delete-button entity [:funds-grid]))]
     (when entity-graph-feature?
       [{:icon :fa-diagram-project
         :toolbar-button?  false
         :dropdown-action? true
         :primary-button?  false
         :on-select (fn [_]
                      (routing/set-view! [:entity-graph :id entity-id]))
         :ord -1
         :label (loc/tr :entity-graph)}])
     [(zap-toolbar/dropdown-button
       {:icon      :fa-star
        :on-select (partial common-actions/toggle-flag entity flagged?)
        :ord       1
        :label     (loc/tr (if flagged? :unflag-entity-menu-item :flag-entity-menu-item))})]

     [(nev-components/audit-trail-button {:entity     entity
                                          :entity-ids [entity-id]})])))

(defn nav-to-lp-report
  [fund-id report-id]
  (rf/dispatch [::lp-report-editor-event/reset-editor report-id fund-id])
  (routing/set-view! [:lp-portal/report-editor :fund-id fund-id :id report-id]))

(defn fund-report-list
  [{:keys [fund-id reporting-admin?]}]
  (let [{:keys [loading? error]} @(rf/subscribe [::sub/report-state fund-id])
        {:keys [can-add-reports?]} @(rf/subscribe [::sub/lp-report-header-state
                                                   fund-id
                                                   reporting-admin?])]
    (cond
      loading? [:div (loc/tr :grid-loading-data)]
      error [alert/danger {} (loc/tr :error-loading-data)]
      :else [fund-ui/fund-report-list
             {:nav-to-report    (fn [report-id] (nav-to-lp-report fund-id report-id))
              :can-add-reports? can-add-reports?}
             @(rf/subscribe [::sub/lp-reports fund-id])])))

(defn stop-reporting-tooltip
  [{:keys [blocker master-funds]}]
  (case blocker
    :master-fund-reports (loc/tr :lp-stop-reporting-master-fund-tooltip
                                 [(->> master-funds
                                       (map :fund/name)
                                       (string/join ", "))])
    :unfinished-reports  (loc/tr :lp-stop-reporting-unfinished-tooltip)
    nil))

(defn lp-report-header-tools
  [{:keys [fund-id reporting-admin?]}]
  (let [{:keys [discontinued? can-add-reports? show-menu? blocker]}
        @(rf/subscribe [::sub/lp-report-header-state fund-id reporting-admin?])]
    (cond
      discontinued?
      [:span
       {:class "text-sm text-base-light-text"}
       (loc/tr :lp-reporting-discontinued)]

      can-add-reports?
      [:div
       {:class "flex items-center gap-1"}
       [button/secondary-open
        {:tooltip (loc/tr :lp-add-reports)}
        [:i.fal.fa-plus-circle
         {:on-click #(rf/dispatch [::event/create-new-lp-report fund-id])}]]
       (when show-menu?
         [dropdown-menu/view
          {:items [{:key       "stop-reporting"
                    :label     (loc/tr :lp-stop-reporting-permanently)
                    :disabled? (some? blocker)
                    :tooltip   (stop-reporting-tooltip blocker)
                    :on-click  #(rf/dispatch [::event/open-stop-reporting fund-id])}]
           :props {:hide-arrow? true
                   :align       :end}
           ;; Same button and icon style as the add buttons of the other modules.
           :trigger-as-child
           [:span
            [button/secondary-open
             {:aria-label (loc/tr :lp-fund-reports-actions)}
             [:i.fal.fa-ellipsis-v]]]}])
       [stop-reporting/modal {:fund-id fund-id}]])))

(def fees-associated-entities-attributes
  [:common/related-entities-company
   :common/related-entities-person])

(defn header-top
  [{:keys [entity read-only?]}]
  [:div
   {:class "flex gap-2"}
   [:span.entity-logo-container.icon-container
    {:style {:padding-left "0"}}
    [:i
     {:class-name (fu/cs :fal (entity/default-icon entity))}]]
   [:div.nev-name-row-container
    [nev-components/name-view
     {:entity     entity
      :read-only? read-only?}]]])

(def default-field-defs
  [{:fields       {0 {:field :fund/description}}
    :field-layout :f-4}])

(defn header-board
  [{:keys [_entity-atom]}]
  (let [header-state (r/atom {:edit-layout? false})
        set-edit-layout! (fn [val] (swap! header-state assoc :edit-layout? val))]
    (fn [{:keys [entity-atom]}]
      (let [edit-layout? (:edit-layout? @header-state)
            entity @entity-atom
            entity-id (:db/id entity)
            field-defs-path [:fund :header :field-defs]
            field-defs-timeout-id :fund-field-defs
            field-defs (or (events/timeout-value field-defs-timeout-id
                                                 @(r/track session/get-view-config field-defs-path))
                           default-field-defs)
            overrides nil
            selected-fields (->> field-defs
                                 (map :fields)
                                 (mapcat vals)
                                 (keep :field))
            remove-fields #{}
            schema (templates/entity-type-template
                    {:entity-type     :entity.type/fund
                     :selected-fields selected-fields
                     :tenant-schema   @session/tenant-schema
                     :account         @session/account
                     :role            @session/role
                     :remove-fields   remove-fields
                     :overrides       overrides})

            ;;test all fields:
            #_#_field-defs
              (->> schema
                   (map first)
                   (partition-all 4 4)
                   (map (fn [fields]
                          (->> fields
                               (map-indexed (fn [i f]
                                              [i {:field f}])))))
                   (map (partial into {}))
                   (map (fn [fields]
                          {:fields       fields
                           :field-layout :f-1-1-1-1}))
                   vec)]
        [:div
         [header/header-board
          {:top              [header-top {:entity entity}]
           :preview-top      [header-top
                              {:entity     entity
                               :read-only? true}]
           :actions          (toolbar-buttons {:entity entity})
           :entity-id        entity-id
           :entity-atom      entity-atom
           :edit-layout?     edit-layout?
           :set-edit-layout! set-edit-layout!
           :field-defs       field-defs
           :schema           schema
           :save-layout!     (fn [new-field-defs]
                               (events/save-field-defs! field-defs-timeout-id
                                                        field-defs-path
                                                        new-field-defs
                                                        (fn [])
                                                        (fn [err]
                                                          (logging/error
                                                           "Failed to save field layout"
                                                           err))))}]]))))

(defn new-header?
  []
  (and (session/has-feature-access? :feature/customizable-nev)
       @session/customizable-nev?))

(defn fund-view
  [{:keys [fund-id] :as _view-args}]
  (let [initialized? (r/atom false)
        state-atom (r/cursor app-db/app-state [:views :nev :fund])
        temp-atom (r/cursor state-atom [:temp])
        view-state-atom (r/cursor state-atom [:view-state])
        error-atom (r/cursor state-atom [:error])
        notification-atom (r/cursor state-atom [:notification])
        events-pane-atom (r/cursor state-atom [:events-pane])
        entity-atom (r/cursor session/entities [fund-id])]
    (r/create-class
     {:component-did-update
      (partial entity-utils/component-did-update
               :fund-id
               (partial events/clear-errors! error-atom notification-atom))
      :component-did-mount
      (fn [_]
        ;; Cached funds still need their reverse-reference graph in both modes.
        (entity-utils/reset-state! state-atom fund-id {:always-fetch? true})
        (reset! initialized? true)
        (rf/dispatch [::controller/get-fund-reports fund-id]))
      :component-will-unmount
      (fn [_]
        (rf/dispatch [::controller/close-fund-reports fund-id])
        (events/clear-errors! error-atom notification-atom)
        (entity-utils/clear-state! state-atom))
      :reagent-render
      (fn [{:keys [fund-id] :as view-args}]
        (let [fund (get @session/entities fund-id)]
          (cond
            (not @initialized?)
            [syncing/view]
            (or (:common/disabled? fund)
                (:not-found @state-atom))
            [not-found/not-found-content]
            (:loading @state-atom)
            [syncing/view]
            :else
            (if (or (not fund)
                    (:common/disabled? fund))
              [:div " "]
              (let [tenant-schema @session/tenant-schema
                    entity-sort (fn-> entity/display-string string/lower-case)
                    fund-holdings (->> @session/holdings
                                       (cu/only-entity :holding/fund fund-id)
                                       (sort-by entity-sort))
                    fund-commitments (->> @session/fund-commitments
                                          (cu/only-entity :fund-commitment/fund fund-id))
                    fund-active-commitments (->> fund-commitments
                                                 (remove :fund-commitment/exit-date)
                                                 (sort-by entity-sort))
                    fund-exited-commitments (->> fund-commitments
                                                 (filter :fund-commitment/exit-date)
                                                 (sort-by entity-sort))
                    fundraising-opportunities (->> @session/fundraising-opportunities
                                                   (cu/filter-by :fundraising-opportunity/fund
                                                                 fund-id))
                    fund-active-fundraising-opportunities
                    (->> fundraising-opportunities
                         (filter (fn [e]
                                   (some #{(:fundraising-opportunity/status e)}
                                         [:fundraising-opportunity.status/active
                                          :fundraising-opportunity.status/on-hold])))
                         (sort-by entity-sort))
                    fund-lost-fundraising-opportunities (->> fundraising-opportunities
                                                             (cu/only-entity
                                                              :fundraising-opportunity/status
                                                              :fundraising-opportunity.status/lost)
                                                             (sort-by entity-sort))
                    fund-oppos (->> @session/oppos
                                    (cu/only-entity :oppo/fund fund-id)
                                    (cu/only-entity :oppo/status :oppo.status/ok)
                                    (sort-by entity-sort))
                    header-fields [:fund/description :fund/pre-marketing-countries]
                    selected-active-commitment-columns [:fund-commitment/investor
                                                        :fund-commitment/cur-committed-amount
                                                        :fund-commitment/committed-date
                                                        :fund-commitment/stake
                                                        :fund-commitment/net-multiple
                                                        :fund-commitment/net-IRR
                                                        (when (plan/available :feature/kyc
                                                                              @session/tenant)
                                                          [:fund-commitment/investor
                                                           :investor/kyc-status])]
                    selected-exited-commitment-columns [:fund-commitment/investor
                                                        :fund-commitment/committed-date
                                                        :fund-commitment/exit-date]
                    selected-active-fundraising-opportunity-columns
                    [:fundraising-opportunity/opportunity-name
                     :fundraising-opportunity/probability-category
                     :fundraising-opportunity/datetime-expected-decision
                     :fundraising-opportunity/cur-commitment-amount--sym
                     :fundraising-opportunity/cur-max-commitment-amount
                     :fundraising-opportunity/cur-min-commitment-amount]
                    selected-lost-fundraising-opportunity-columns
                    [:fundraising-opportunity/opportunity-name
                     :fundraising-opportunity/lost-on
                     :fundraising-opportunity/lost-reason
                     :fundraising-opportunity/lost-comment
                     :fundraising-opportunity/owner]
                    selected-oppo-columns [:oppo/name :oppo/owner :oppo/stage
                                           :oppo/status-and-next-steps]
                    selected-holding-columns [:holding/name :holding/cur-valuation
                                              :holding/exit-plan]
                    expanded-holding-columns selected-holding-columns
                    view-id :fund
                    module-collapsed-path (fn [module-id]
                                            [view-id :modules :module-config module-id :collapsed?])
                    toolbar-back-to-label (loc/tr :navbar-item/managed-funds)
                    all-teams @session/teams
                    account-teams (teams/resolve-account-teams @session/account all-teams)]
                [:<>
                 (when-not (session/is-guest?)
                   [zap-toolbar/nev-toolbar
                    {:state           state-atom
                     :label           (partial zap-toolbar/toolbar-back
                                               [:funds-grid]
                                               toolbar-back-to-label)
                     :actions         (toolbar-buttons (assoc view-args
                                                              :entity
                                                              fund))
                     :dropdown-toggle [:span (loc/tr :actions)
                                       [:i.fas.fa-angle-down.ml-2]]
                     :dropdown-toggle-class-name
                     "zap-button zap-button--primary-btn actions-dropdown"}])
                 [:div.entity__container
                  (when @error-atom
                    [nev-components/error-bar error-atom])
                  (when @notification-atom
                    [nev-components/notification-bar notification-atom])

                  [:div.entity
                   [:div
                    {:class "w-full max-w-[1920px] p-4"}
                    (if (new-header?)
                      [header-board {:entity-atom entity-atom}]
                      [nev-components/header-module
                       {:component-state temp-atom
                        :view-state      view-state-atom
                        :entity          fund
                        :selected-fields header-fields}])
                    [:div.md:grid.grid-cols-10.gap-4
                     [:div
                      {:class (if (session/event-pane-disabled?)
                                "col-span-10"
                                "col-span-10 lg:col-span-5")}
                      [:div.entity-left
                       (when (session/new-grid-2023?)
                         [investor-references/view
                          {:fund-id fund-id
                           :commitment-ids (mapv :db/id fund-commitments)}])
                       [modules/view
                        {:view-id  view-id
                         :ordering (get-in @session/view-config [view-id :modules :ordering])
                         :save-modules-order! (partial events/save-modules-order!
                                                       (:entity/type fund))
                         :modules
                         (concat
                          [{:module-id  :fund-information
                            :header     (loc/tr :nev-module/about-this-entity-type [(loc/tr :fund)])
                            ;;TODO uncomment when finished:
                            #_:header-tools
                            #_(when (session/is-admin?)
                                [:span.entity-editor-header-settings
                                 {:on-click (fn [e]
                                                ;;open settings
                                              )}
                                 [:span (loc/tr :entity-editor/settings-label)]
                                 [:i.fal.fa-cog]])
                            :collapsed? (get-in @session/view-config
                                                (module-collapsed-path :fund-information))
                            :save-module-collapsed! (partial events/save-module-collapsed!
                                                             (:entity/type fund))
                            :content    [:div
                                         [entity-editor/view
                                          {:state-atom    state-atom
                                           :entity-id     fund-id
                                           :view-id       view-id
                                           :module-id     :fund-information
                                           :remove-fields (concat [:fund/name :common/network-drive :common/named-network-drives]
                                                                  (field/entity-tag-fields
                                                                   tenant-schema
                                                                   (:entity/type fund))
                                                                  header-fields)
                                           :ordering      (get-in
                                                           @session/view-config
                                                           [view-id :modules :module-config
                                                            :fund-information :ordering])}]]}
                           (nev-components/entity-listing-module
                            {:module-id      :target-fund-in-active-commitments
                             :view-id        view-id
                             :module-title   (loc/tr :nev-module/fund-active-commitments)
                             :entity         fund
                             :shown-entities fund-active-commitments
                             :shown-columns  (->>
                                              selected-active-commitment-columns
                                              (map (fn [k]
                                                     {:key   k
                                                      :label (let
                                                              [fs (registry/get-field-schema
                                                                   tenant-schema
                                                                   k)]
                                                               (loc/field-name k fs))})))})
                           (nev-components/entity-listing-module
                            {:module-id      :target-fund-in-exited-commitments
                             :view-id        view-id
                             :module-title   (loc/tr :nev-module/fund-exited-commitments)
                             :entity         fund
                             :shown-entities fund-exited-commitments
                             :shown-columns  (->>
                                              selected-exited-commitment-columns
                                              (map (fn [k]
                                                     {:key   k
                                                      :label (let
                                                              [fs (registry/get-field-schema
                                                                   tenant-schema
                                                                   k)]
                                                               (loc/field-name k fs))})))})
                           (nev-components/entity-listing-module
                            {:module-id      :target-fund-in-active-fundraising-opportunities
                             :view-id        view-id
                             :module-title   (loc/tr :nev-module/fund-active-fundraising-opportunities)
                             :entity         fund
                             :shown-entities fund-active-fundraising-opportunities
                             :shown-columns  (->>
                                              selected-active-fundraising-opportunity-columns
                                              (map (fn [k]
                                                     {:key   k
                                                      :label (let [fs (registry/get-field-schema
                                                                       tenant-schema
                                                                       k)]
                                                               (loc/field-name k fs))})))})
                           (nev-components/entity-listing-module
                            {:module-id      :target-fund-in-lost-fundraising-opportunities
                             :view-id        view-id
                             :module-title   (loc/tr :nev-module/fund-lost-fundraising-opportunities)
                             :entity         fund
                             :shown-entities fund-lost-fundraising-opportunities
                             :shown-columns  (->>
                                              selected-lost-fundraising-opportunity-columns
                                              (map (fn [k]
                                                     {:key   k
                                                      :label (let [fs (registry/get-field-schema
                                                                       tenant-schema
                                                                       k)]
                                                               (loc/field-name k fs))})))})
                           (nev-components/entity-listing-module
                            {:module-id      :target-fund-in-oppos
                             :view-id        view-id
                             :module-title   (loc/tr :nev-module/fund-oppos)
                             :entity         fund
                             :shown-entities fund-oppos
                             :shown-columns  (->>
                                              selected-oppo-columns
                                              (map (fn [k]
                                                     {:key   k
                                                      :label (let [fs (registry/get-field-schema
                                                                       tenant-schema
                                                                       k)]
                                                               (loc/field-name k fs))})))})
                           (nev-components/entity-listing-module
                            {:module-id        :target-fund-in-holdings
                             :view-id          view-id
                             :module-title     (loc/tr :nev-module/fund-holdings)
                             :entity           fund
                             :shown-entities   fund-holdings
                             :show-totals?     true
                             :expanded-columns (->>
                                                expanded-holding-columns
                                                (map (fn [k]
                                                       {:key   k
                                                        :label (let [fs (registry/get-field-schema
                                                                         tenant-schema
                                                                         k)]
                                                                 (loc/field-name k fs))})))
                             :shown-columns    (->>
                                                selected-holding-columns
                                                (map (fn [k]
                                                       {:key   k
                                                        :label (let
                                                                [fs (registry/get-field-schema
                                                                     tenant-schema
                                                                     k)]
                                                                 (loc/field-name k fs))}))
                                                (doall))})]

                          [(when (session/has-module-access? @session/account :module/lp-reporting)
                             (let [reporting-admin? (boolean
                                                     (or (some (set (:fund/reporting-admins fund))
                                                               (map :db/id account-teams))
                                                         (session/is-admin?)))
                                   {:keys [discontinued? can-add-reports?]}
                                   @(rf/subscribe [::sub/lp-report-header-state fund-id reporting-admin?])]
                               {:module-id    :fund-lp-reports
                                :header       (loc/tr :fund-reports)
                                ;; Only when the tools show something. Empty tools still
                                ;; stop a click on the header from collapsing the module.
                                :header-tools (when (or discontinued? can-add-reports?)
                                                [lp-report-header-tools
                                                 {:fund-id          fund-id
                                                  :reporting-admin? reporting-admin?}])
                                :collapsed?   (get-in @session/view-config
                                                      (module-collapsed-path :fund-lp-reports))
                                :save-module-collapsed! (partial events/save-module-collapsed!
                                                                 (:entity/type fund))
                                :content      [fund-report-list
                                               {:fund-id          fund-id
                                                :reporting-admin? reporting-admin?}]}))]
                          (when-not (session/is-guest?)
                            (associated-entities/modules
                             {:entity-id fund-id
                              :save-module-collapsed! events/save-module-collapsed!
                              :module-collapsed-path module-collapsed-path}))
                          (when (session/fee-tracking?)
                            (fees/modules {:entity-id fund-id
                                           :save-module-collapsed! events/save-module-collapsed!
                                           :module-collapsed-path module-collapsed-path
                                           :fees-associated-entities-attributes
                                           fees-associated-entities-attributes}))
                          [(when (and (not (session/is-mobile?))
                                      (f-permissions/field-read-access? :common/attachments
                                                                        :entity.type/fund
                                                                        nil))
                             (nev-components/attachments-module view-id
                                                                :fund-attachments
                                                                fund))])}]]]
                     (when-not (session/event-pane-disabled?)
                       [:div
                        {:class "col-span-10 lg:col-span-5"}
                        [event-pane/view
                         {:state       events-pane-atom
                          :entity-ids  [fund-id]
                          :module-args {:state-atom state-atom
                                        :view-id    view-id}}]])]]]]])))))})))

(defmethod routing/render-view :entity.type/fund
  [{:keys [route-params]}]
  (let [fund-id (-> route-params
                    :id
                    js/parseInt)]
    [default-page-layout/view
     {:class-name "zap-page fund"}
     ;;the key change forces rerendering of the component (and its child components)
     ^{:key (str "fund-viewer-id-" fund-id)}
     [fund-view {:fund-id fund-id}]]))
